
import {
  defineComponent,
  inject,
  reactive,
  toRefs,
  onBeforeMount,
  onMounted,
} from "vue";
import AfterLoginPageLayout from "@/views/layouts/AfterLoginPageLayout.vue";
import FlatButton from "@/components/atomics/FlatButton.vue";
import TextButton from "@/components/atomics/TextButton.vue";
import HeaderTitleBar from "@/components/parts/HeaderTitleBar.vue";
import FilePreview from "@/components/parts/FilePreview.vue";
import PdfViewer from "@/components/atomics/pdf-viewer/PdfViewer.vue";
import AvatarCircle from "@/components/organisms/AvatarCircle.vue";
import { ContractRepository, RepositoryFactory } from "@/lib/https";

import { getFilePathFromUrl } from "@/lib/utility/stringUtil";
import { useI18n } from "vue-i18n";
import { useRouter, useRoute } from "vue-router";
import useSignFunctions from "@/lib/compositional-logic/useSignFunctions";
import { useStore } from "vuex";
import { ReadWriteRole, canContractUpdate } from "@/lib/utility/permission";
import ContractInformationReadonly from "@/components/parts/ContractInformationReadonly.vue";
import useContractFromTemplate from "@/lib/compositional-logic/useContractFromTemplate";
import SideAccountVars from "@/components/organisms/SideAccountVars.vue";
import signEnum from "@/models/signEnum";

export default defineComponent({
  components: {
    FlatButton,
    TextButton,
    HeaderTitleBar,
    FilePreview,
    AfterLoginPageLayout,
    PdfViewer,
    AvatarCircle,
    ContractInformationReadonly,
    SideAccountVars,
  },
  name: "SignDone",
  props: {
    contractId: { type: String, default: "" },
    isGuest: { type: Boolean, default: false },
    isTransferSuccess: { type: String, default: "false" },
  },
  setup(props) {
    const { t } = useI18n();
    const isMobile = inject("isMobile");
    const state = reactive({
      isShowAdvanced: true,
      customField: [] as any,
      isCopied: false,
      automaticOptions: [
        {
          id: "true",
          name: t("template.updateOption1"),
        },
        {
          id: "false",
          name: t("template.updateOption2"),
        },
      ],
      contractState: {} as any,
      files: [] as any,
      selectedFileIndex: 0,
      isAllSigned: false,
      canContractUpdate: false,
      canContractInformationUpdate: false,
      contractDetailInfo: {
        contractOverview: "",
        contractConclusionDate: null as Date | null,
        contractStartDate: null as Date | null,
        contractEndDate: null as Date | null,
        terminationNoticePeriod: "",
        isAutomaticallyUpdated: "null",
        controlNumber: "",
        transactionAmount: "",
        freeText: "",
      } as any,
    });

    const sideContractState = reactive({
      fileData: [] as any,
    });

    const router = useRouter();
    const route = useRoute();
    const store = useStore();

    const { getContractFullInformation, getGuestContractInformation } =
      signDoneMethods();
    const { getAccessCode } = useSignFunctions();
    const { loadContractDetailInfo } = useContractFromTemplate();

    const showSuccessNotification = (text: string) => {
      store.commit("notification/showSuccessNotification", text);
    };

    const navigateToFolder = () => {
      router.push({
        name: "Folder",
        query: {
          folderId: state.contractState.folderId || "",
          folderTitle: state.contractState.folderTitle || "",
          status: route.params.status,
          orderBy: route.params.orderBy,
        },
      });
    };

    const createCsvString = () => {
      let csvStr = t("sign.csvHead1") + ","+ t("sign.csvHead2") + "\n";
      sideContractState.fileData.forEach((file: any) => {
        csvStr += file.fileName;
        file.accountVars.forEach((item: any) => {
          csvStr += ",";
          if (item.type === signEnum.signatureFieldType.CHECKBOX_FIELD) {
            csvStr += item.isChecked ? "true" : "false"
          } else {
            csvStr += item.text.replace(/\r?\n/g,'')
          }
        });
        csvStr += "\n"
      });
      return csvStr;
    }

    onMounted(() => {
      if (props.isTransferSuccess === "true") {
        showSuccessNotification(t("notification.updateSuccess"));
      }
    });

    onBeforeMount(async () => {
      try {
        let payload;

        const accessCode = getAccessCode(props.contractId);
        if (props.isGuest) {
          payload = await getGuestContractInformation(accessCode);
        } else {
          payload = await getContractFullInformation(
            props.contractId,
            accessCode
          );
        }

        state.contractState = { ...payload };

        state.contractDetailInfo = loadContractDetailInfo(
          payload.contractInformation
        );

        state.contractState.contractFiles.forEach((contractFile: any) => {
          if (contractFile.contractVars) {
            const accountVars = (contractFile.contractVars as any[]).sort((var1, var2) => var1.contractSignerOrder - var2.contractSignerOrder)

            sideContractState.fileData.push({
              fileName: contractFile.fileName,
              accountVars: accountVars,
            });
          }
        });

        let rejectSignerOrder = 0;
        if (state.contractState.status === 4) { // rejected
          state.contractState.contractSigners.forEach((signer: any) => {
            if (signer.status === 3) rejectSignerOrder = signer.order;
          });

          sideContractState.fileData.forEach((file: any) => {
            file.accountVars.forEach((account: any) => {
              if (account.contractSignerOrder === rejectSignerOrder) account.text = "";
            })
          })
        };

        state.isAllSigned =
          payload.contractSigners &&
          payload.countSigned === payload.contractSigners.length;
        state.files = payload.contractFiles.map((file: any) => ({
          id: file.id,
          name: file.fileName,
          path: getFilePathFromUrl(file.filePath),
          type: file.fileType,
        }));
        state.canContractUpdate = canContractUpdate(
          state.contractState.canUpdate,
          state.contractState.countSigned,
          state.contractState.status,
          state.contractState.countFileSigned
        );
        state.canContractInformationUpdate =
          state.contractState.role == ReadWriteRole.READ_WRITE;
      } catch (e: any) {
        const errorMessage = e?.data?.message || "";
        router.push({
          name: "ErrorPage",
          query: {
            error: errorMessage,
          },
        });
      }
    });

    return {
      ...toRefs(state),
      ...toRefs(sideContractState),
      isMobile,
      getFilePathFromUrl,
      navigateToFolder,
      createCsvString,
    };
  },
  methods: {
    download(url: string) {
      let downloadPoint = document.createElement("a");
      downloadPoint.href = url;
      downloadPoint.target = "_blank";
      downloadPoint.click();
    },
    downloadCsv() {
      const csvBlob = new Blob([this.createCsvString()], {type:"text/csv"});
      const downloadPoint = document.createElement("a");
      downloadPoint.href = URL.createObjectURL(csvBlob);
      downloadPoint.download = "input.csv";
      downloadPoint.click();
    },
    copyToClipboard() {
      const copyData = this.createCsvString();
      navigator.clipboard.writeText(copyData);
      this.isCopied = true;
      setTimeout(() => {
        this.isCopied = false;
      }, 1000);
    }
  },
  beforeRouteLeave(to, from, next) {
    if (
      to.fullPath.includes("/sign/details") ||
      to.fullPath.includes("/guest/contract-guest")
    ) {
      next({
        name: "ErrorPage",
        query: {
          error: this.$t("errors.errorBackToSignAgain"),
        },
      });
    } else next();
  },
});

const signDoneMethods = () => {
  const { getContractFullInformation, getGuestContractInformation } =
    RepositoryFactory.getRepository<ContractRepository>(ContractRepository);

  return {
    getContractFullInformation,
    getGuestContractInformation,
  };
};
